import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { useProducts, usePromoCodes, useSettings } from "../hooks/useCatalog";
import { isPromoValidNow, eligibleSubtotal } from "./promo";

const CartContext = createContext(null);

const STORAGE_KEY = "hgh_cart_v1";
const DEFAULT_DELIVERY_FEE = 15;
const DEFAULT_FREE_THRESHOLD = 200;

export function CartProvider({ children }) {
  // Live catalog + config from Firestore (cached by react-query).
  const { data: products } = useProducts();
  const { data: promos } = usePromoCodes();
  const { data: settings } = useSettings();

  const deliveryFeeFlat = settings?.defaultDeliveryFee ?? DEFAULT_DELIVERY_FEE;
  const freeDeliveryThreshold = settings?.freeDeliveryThreshold ?? DEFAULT_FREE_THRESHOLD;

  const productMap = useMemo(() => {
    const map = {};
    (products || []).forEach((p) => {
      map[p.id] = p;
    });
    return map;
  }, [products]);

  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [appliedPromo, setAppliedPromo] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore
    }
  }, [items]);

  const addItem = useCallback((productId, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.productId === productId);
      if (existing) {
        return prev.map((i) =>
          i.productId === productId ? { ...i, qty: i.qty + qty } : i
        );
      }
      return [...prev, { productId, qty }];
    });
  }, []);

  const updateQty = useCallback((productId, qty) => {
    setItems((prev) =>
      prev
        .map((i) => (i.productId === productId ? { ...i, qty: Math.max(1, qty) } : i))
        .filter((i) => i.qty > 0)
    );
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => prev.filter((i) => i.productId !== productId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setAppliedPromo(null);
  }, []);

  const detailed = useMemo(() => {
    return items
      .map((i) => {
        const product = productMap[i.productId];
        if (!product) return null;
        const unitPrice = product.discountPrice ?? product.price;
        return { ...i, product, unitPrice, lineTotal: unitPrice * i.qty };
      })
      .filter(Boolean);
  }, [items, productMap]);

  const subtotal = useMemo(
    () => detailed.reduce((sum, i) => sum + i.lineTotal, 0),
    [detailed]
  );

  const itemCount = useMemo(
    () => items.reduce((sum, i) => sum + i.qty, 0),
    [items]
  );

  // Keep an applied promo honest AFTER apply-time too: if a later cart change
  // (qty down, item removed) drops the subtotal below its minimum — or the code
  // expires mid-session — clear it. Without this, the code would still read as
  // "applied" while yielding no discount (the exact state the min-check removes
  // at apply-time). appliedPromo is not persisted, so this never fires on load.
  useEffect(() => {
    if (!appliedPromo) return;
    const belowMin = subtotal < (appliedPromo.minPurchase || 0);
    const noEligible = eligibleSubtotal(appliedPromo, detailed) <= 0;
    if (belowMin || noEligible || !isPromoValidNow(appliedPromo)) {
      setAppliedPromo(null);
      toast(
        belowMin
          ? `Promo ${appliedPromo.code} removed — minimum spend no longer met`
          : noEligible
          ? `Promo ${appliedPromo.code} removed — no eligible items in cart`
          : `Promo ${appliedPromo.code} is no longer valid`
      );
    }
  }, [appliedPromo, subtotal, detailed]);

  // Client-side apply. Mirrors the server's validatePromo (active flag, date
  // window, usage limit) AND the minimum-purchase requirement — so we never show
  // a code as "applied" when it won't actually discount. Returns a reason on
  // failure so the UI can tell the customer exactly why (e.g. min spend not met).
  const applyPromo = useCallback(
    (code) => {
      const found = (promos || []).find(
        (p) => p.code.toLowerCase() === code.trim().toLowerCase()
      );
      if (!found || !isPromoValidNow(found)) {
        setAppliedPromo(null);
        return { ok: false, reason: "invalid" };
      }
      const min = found.minPurchase || 0;
      if (subtotal < min) {
        setAppliedPromo(null);
        return { ok: false, reason: "min", minPurchase: min, shortfall: min - subtotal };
      }
      // Category-/product-scoped promos only discount matching items — reject if
      // the cart has none (otherwise the code would apply for $0 discount).
      if (eligibleSubtotal(found, detailed) <= 0) {
        setAppliedPromo(null);
        return { ok: false, reason: "noEligible" };
      }
      setAppliedPromo(found);
      return { ok: true, promo: found };
    },
    [promos, subtotal, detailed]
  );

  const clearPromo = useCallback(() => setAppliedPromo(null), []);

  const discount = useMemo(() => {
    // Re-check validity here too: a code can expire while it sits in the cart.
    if (!appliedPromo || !isPromoValidNow(appliedPromo)) return 0;
    if (subtotal < (appliedPromo.minPurchase || 0)) return 0;
    // Restricted promos discount only their eligible items; unrestricted promos
    // have base === subtotal, so behaviour is unchanged for them.
    const base = eligibleSubtotal(appliedPromo, detailed);
    if (base <= 0) return 0;
    let d = 0;
    if (appliedPromo.type === "Percentage") {
      d = (base * appliedPromo.value) / 100;
    } else {
      d = appliedPromo.value;
    }
    if (appliedPromo.maxDiscount) d = Math.min(d, appliedPromo.maxDiscount);
    return Math.min(d, base);
  }, [appliedPromo, subtotal, detailed]);

  const deliveryFee = useMemo(() => {
    if (subtotal === 0) return 0;
    if (subtotal >= freeDeliveryThreshold) return 0;
    return deliveryFeeFlat;
  }, [subtotal, freeDeliveryThreshold, deliveryFeeFlat]);

  const total = useMemo(
    () => Math.max(0, subtotal - discount + deliveryFee),
    [subtotal, discount, deliveryFee]
  );

  const value = {
    items,
    detailed,
    itemCount,
    subtotal,
    discount,
    deliveryFee,
    total,
    appliedPromo,
    freeDeliveryThreshold,
    addItem,
    updateQty,
    removeItem,
    clearCart,
    applyPromo,
    clearPromo,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
